/**
 * El mismo recibo, pero como líneas de texto en vez de bytes para la impresora.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ EXISTE ESTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo pidió el dueño después de comprarse una impresora: «tiene que funcionar
 * como Fina — en Fina se puede imprimir sin instalar drivers ni nada».
 *
 * Y es exactamente el punto débil de Softfi. Había dos caminos y los dos
 * pinchaban en Windows:
 *
 *   1. **WebUSB** (`escpos.ts`): le habla a la impresora byte a byte. Es el
 *      mejor —sin diálogo y con corte automático— pero en Windows, en cuanto la
 *      impresora tiene su driver instalado, **el sistema la reserva y no la
 *      suelta**. Para usarla desde el navegador hay que reemplazar el driver
 *      por WinUSB con Zadig, y eso la saca de la lista de impresoras de Windows
 *      para todo lo demás. Pedirle eso a un bodeguero es perderlo.
 *
 *   2. **La hoja HTML** (`imprimirHoja.ts`): imprime por el camino normal del
 *      sistema, sin tocar drivers. Pero el tamaño del papel lo pide con
 *      `@page { size: 80mm ... }`, y esa regla el navegador **la descarta en
 *      silencio** si el destino elegido no admite ese tamaño. Con «Fax» o
 *      «Guardar como PDF» seleccionado, el recibo sale diminuto en una hoja
 *      carta. Es lo que se vio en la captura.
 *
 * Lo que hace Fina es lo tercero: **generar un PDF del tamaño del recibo**. Un
 * PDF lleva su geometría dentro (el `MediaBox`), así que no depende de que el
 * destino la acepte ni de que el navegador respete una regla de CSS. Se imprime
 * con el driver normal, el que Windows instaló solo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ Y POR QUÉ NO SE ESCRIBE EL RECIBO OTRA VEZ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque acabaríamos con dos recibos distintos. El día que se añada una línea
 * —un impuesto nuevo, el nombre del vendedor— se añadiría en uno y no en el
 * otro, y nadie se daría cuenta hasta que un cliente comparase dos papeles.
 *
 * Esta clase tiene **los mismos métodos** que `Ticket`, así que `construirTicket`
 * sirve para las dos: una escupe bytes y la otra líneas. Una sola composición,
 * dos salidas.
 */

import type { AnchoPapel } from "./escpos";

/** Cuántos caracteres caben por línea en cada rollo. Igual que en `escpos.ts`. */
const COLUMNAS: Record<AnchoPapel, number> = { "58": 32, "80": 48 };

/** Una línea del recibo, con la pinta que tiene que tener. */
export type LineaDeTicket = {
  texto: string;
  alineacion: "izquierda" | "centro" | "derecha";
  negrita: boolean;
  /** A doble tamaño: el nombre del negocio y el TOTAL. */
  doble: boolean;
};

/**
 * Construye el recibo como líneas de texto.
 *
 * ⚠️ Su interfaz imita a `Ticket` a propósito, método por método. Lo que no
 * tiene sentido en papel normal —abrir el cajón, cortar, la tabla de
 * caracteres— se acepta y no hace nada, para que `construirTicket` pueda
 * llamarlo sin preguntar con quién está hablando.
 */
export class TicketEnLineas {
  private lineas: LineaDeTicket[] = [];
  private readonly columnasPorLinea: number;

  private alineacion: LineaDeTicket["alineacion"] = "izquierda";
  private enNegrita = false;
  private escalaAncho = 1;

  constructor(ancho: AnchoPapel = "80") {
    this.columnasPorLinea = COLUMNAS[ancho];
  }

  /** Las líneas listas para dibujar. */
  resultado(): LineaDeTicket[] {
    return this.lineas;
  }

  /** Cuántos caracteres caben ahora (la mitad si el texto va a doble ancho). */
  get anchoLinea(): number {
    return Math.floor(this.columnasPorLinea / this.escalaAncho);
  }

  /** Las columnas del rollo, para que quien dibuje sepa cuánto mide de ancho. */
  get columnasDelPapel(): number {
    return this.columnasPorLinea;
  }

  // --- Estado (lo mismo que en la térmica) ---------------------------------

  iniciar(): this {
    return this;
  }

  izquierda(): this {
    this.alineacion = "izquierda";
    return this;
  }

  centrar(): this {
    this.alineacion = "centro";
    return this;
  }

  derecha(): this {
    this.alineacion = "derecha";
    return this;
  }

  negrita(activa = true): this {
    this.enNegrita = activa;
    return this;
  }

  doble(): this {
    this.escalaAncho = 2;
    return this;
  }

  normal(): this {
    this.escalaAncho = 1;
    this.enNegrita = false;
    return this;
  }

  // --- Escribir ------------------------------------------------------------

  linea(t = ""): this {
    this.lineas.push({
      texto: t,
      alineacion: this.alineacion,
      negrita: this.enNegrita,
      doble: this.escalaAncho === 2,
    });
    return this;
  }

  salto(cantidad = 1): this {
    for (let i = 0; i < cantidad; i++) this.linea("");
    return this;
  }

  /**
   * Algo a la izquierda y algo a la derecha, con espacios en medio.
   *
   * ⚠️ Copiado tal cual de `Ticket`, hasta el recorte. Si aquí se rellenara
   * distinto, el papel del PDF y el de la térmica no cuadrarían columna con
   * columna — y el dueño que compare los dos pensaría que uno está mal.
   */
  columnas(izquierda: string, derecha: string, relleno = " "): this {
    const total = this.anchoLinea;
    const der = derecha.slice(0, total);
    const espacioIzq = total - der.length;
    const izq = izquierda.length > espacioIzq ? izquierda.slice(0, espacioIzq) : izquierda;
    const huecos = Math.max(0, total - izq.length - der.length);
    return this.linea(izq + relleno.repeat(huecos) + der);
  }

  /** Un texto largo partido por palabras, sin cortar ninguna por la mitad. */
  parrafo(t: string): this {
    const total = this.anchoLinea;
    let actual = "";

    for (const palabra of t.split(/\s+/).filter(Boolean)) {
      if (actual === "") {
        actual = palabra;
      } else if (actual.length + 1 + palabra.length <= total) {
        actual += " " + palabra;
      } else {
        this.linea(actual);
        actual = palabra;
      }
      // Una palabra sola más larga que la línea (un correo, una URL): se parte.
      while (actual.length > total) {
        this.linea(actual.slice(0, total));
        actual = actual.slice(total);
      }
    }

    if (actual !== "") this.linea(actual);
    return this;
  }

  separador(caracter = "-"): this {
    return this.linea(caracter.repeat(this.anchoLinea));
  }

  // --- Lo que solo existe en la térmica ------------------------------------
  //
  // ⚠️ Se aceptan y no hacen nada. `construirTicket` los llama sin saber con
  // quién habla, y esa es justo la gracia: una sola composición para las dos
  // salidas. Devolver `this` mantiene el encadenado.

  avanzar(): this {
    return this;
  }

  cortar(): this {
    return this;
  }

  abrirCajon(): this {
    return this;
  }

  /**
   * El logo.
   *
   * ⚠️ En el PDF no se dibuja, y es una decisión: meter una imagen dentro de un
   * PDF escrito a mano obliga a llevar un codificador de imágenes encima, y lo
   * que el cliente necesita del papel —qué compró, cuánto pagó y a quién
   * reclamar— es texto. El nombre del negocio va debajo del logo en la térmica
   * justamente porque un logo no se lee.
   */
  imagen(): this {
    return this;
  }
}
